/**
 * ExplainIssueModal — read-only AI explanation of a compile/structural issue.
 *
 * Called from ResolveIssuesView when the user clicks "Explain Issue".
 * The parent calls customPromptAI with a plain-English explanation prompt
 * (no JSON patch required) and passes the result here as a plain string.
 *
 * Props:
 *   isOpen    — bool
 *   onClose   — () → void
 *   issue     — { summary, javaLocation: { file, line } } | null
 *   loading   — bool — AI request in flight
 *   result    — { response } | null — plain-text explanation from AI
 *   error     — string | null
 */

import { Lightbulb, X, Loader2, AlertTriangle } from 'lucide-react'
import DraggableModal from './DraggableModal'
import './AIResponseModal.css'
import './AISuggestionModal.css'
import './ExplainIssueModal.css'

export default function ExplainIssueModal({ isOpen, onClose, issue, loading, result, error }) {
  if (!isOpen) return null

  const text = result?.response || result?.code || null

  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <DraggableModal className="ai-modal explain-issue-modal" onClose={onClose} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="ai-modal-header">
          <div className="ai-modal-title">
            <Lightbulb size={18} />
            <span>Explain Issue</span>
          </div>

          {issue && (
            <div className="ais-context-chip">
              <code>{issue.javaLocation?.file || 'unknown'}</code>
              {issue.javaLocation?.line && <span>:{issue.javaLocation.line}</span>}
            </div>
          )}

          <button className="ai-modal-close" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>

        {/* ── Content ── */}
        <div className="ai-modal-content explain-issue-body">

          {/* Issue summary banner */}
          {issue?.summary && (
            <div className="ais-issue-banner">
              <AlertTriangle size={12} />
              <span>{issue.summary}</span>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="ai-modal-loading">
              <Loader2 size={28} className="spinning" />
              <p>Generating explanation…</p>
              <span>Claude is analyzing the issue</span>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="ais-error">
              <AlertTriangle size={13} />
              <span>{error}</span>
            </div>
          )}

          {/* Explanation text */}
          {!loading && text && (
            <div className="ai-explanation">
              <pre className="explain-issue-text">{text}</pre>
            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="ai-modal-footer">
          <div className="ai-modal-footer-right">
            <button className="ai-modal-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

      </DraggableModal>
    </div>
  )
}
